/**
 * Sales > Targeting: the design's targeting logic (salesVals L20260-20307 and the tg*
 * values L20684-20913) over REAL rows: the metro's pilot-priority board
 * (GET /api/financial/facilities/targeting) and its modeled open dollars
 * (GET /api/financial/facilities/reimbursement-opportunity). Pure: no React, no fetch.
 *
 * The design joins three sample lists (TOP, WORST, BOARD) by name; here they are joined
 * by facility id. Nothing per metro is written here: the metro, its counts, its dollars
 * and its distribution all come from the rows.
 */
import type { TgOpportunityResponse, TgOpportunityRow, TgTargetingResponse, TgTargetingRow } from '@aithora/shared';

export type Tier = 'A' | 'B' | 'C';
/** 'top' = the ten largest open dollars, 'skip' = the ten smallest, 'mid' = the rest of the
 *  metro's sized facilities, 'board' = scored but not sized, 'search' = a catalog hit. */
export type ListKind = 'top' | 'skip' | 'mid' | 'board' | 'search';

export interface Prospect {
  key: string;
  name: string;
  city: string;
  state: string;
  type: string;
  /** "200 residents" (sized) or "118 beds". */
  size: string;
  beds: number | null;
  residents: number | null;
  /** "$459K" or null when the facility is not sized. */
  amount: string | null;
  /** Open dollars in thousands (the design's amountN). */
  amountN: number;
  /** "$268K Part B" */
  per: string;
  est: boolean;
  list: ListKind;
  listRank: number;
  row: TgTargetingRow | null;
  /** ICP, Urgency, Ease, Strategic on 0-100 (the engine's 1-5 mapped the engine's way). */
  dims: [number, number, number, number] | null;
  warmth: number;
  tier: Tier | null;
  score: number | null;
  geo: { lat: number; lng: number } | null;
  ehr: string | null;
}

/** The design's tier colours (TC, L20713) and the board's tier chip tones (TIER_TONE, L20306). */
export const TC: Record<Tier, string> = { A: '#1F6B52', B: '#2F5C97', C: '#5B6B7C' };
export const TIER_TONE: Record<Tier, [string, string]> = { A: ['#12503C', 'rgba(47,138,111,.16)'], B: ['#5B6B7C', 'rgba(91,107,124,.18)'], C: ['#54606C', 'rgba(34,39,46,.08)'] };
export const DIMS = ['ICP', 'Urgency', 'Ease', 'Strategic', 'Warmth'] as const;
/** How many facilities the "Uncaptured $" and "Skip for now" lists hold (design TOP / WORST). */
export const LIST_N = 10;

/** The design's tc(): an all-capitals catalog name in title case (LLC and LP kept). */
export const tc = (s: string) =>
  s === s.toUpperCase()
    ? s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()).replace(/\bLlc\b/g, 'LLC').replace(/\bLp\b/g, 'LP')
    : s;

/** $459K, $96.5M; an en dash when there is no figure. */
export function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '–';
  if (Math.abs(n) >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  return '$' + Math.round(n / 1000) + 'K';
}

/** The engine's 1-5 sub-score on the page's 0-100 scale, mapped as the engine maps its priority ((d-1)/4). */
export const dim100 = (d: number) => Math.round(((Math.max(1, Math.min(5, d)) - 1) / 4) * 100);

/** "austin" as a label when the server sends no display name. */
export const metroLabel = (t: TgTargetingResponse | null): string => {
  if (!t) return '';
  if (t.meta.label) return t.meta.label;
  return (t.meta.metro || '').replace(/(^|[\s-])[a-z]/g, (c) => c.toUpperCase());
};

/**
 * "139 residents (CMS)": the census Sales reads is the CMS catalog's (or an estimate when the
 * row carried none), not the building's live roster the Overview counts, so it says so.
 */
export const residentsWord = (residents: number, estimated: boolean) => `${residents} residents (${estimated ? 'estimated' : 'CMS'})`;
const sizeOf = (beds: number | null, residents: number | null, estimated = false) =>
  residents != null && residents > 0 ? residentsWord(residents, estimated) : beds != null ? beds + ' beds' : '';

/**
 * Every facility the page knows about, in the design's order (TOP, then WORST, then the
 * board's own): the metro's sized facilities by open dollars (largest first), then the
 * smallest ones not already listed, then scored facilities with no size. A facility with no
 * census (an agency) has no open dollars and is left out of the dollar lists, as the
 * design's strip says.
 */
export function buildProspects(top: TgOpportunityResponse | null, low: TgOpportunityResponse | null, board: TgTargetingRow[]): Prospect[] {
  const byId = new Map<string, Prospect>();
  const out: Prospect[] = [];
  const rowById = new Map(board.map((r) => [r.id, r]));
  const sized = (r: TgOpportunityRow) => !!r.facilityId && r.residents > 0;
  const topRows = (top?.facilities ?? []).filter(sized);
  const topIds = new Set(topRows.slice(0, LIST_N).map((r) => r.facilityId as string));
  const skipRows = (low?.facilities ?? []).filter((r) => sized(r) && !topIds.has(r.facilityId as string)).slice(0, LIST_N);
  const skipIds = new Set(skipRows.map((r) => r.facilityId as string));

  const fromOpp = (r: TgOpportunityRow, list: ListKind, rank: number) => {
    const id = r.facilityId as string;
    const t = rowById.get(id) ?? null;
    const p: Prospect = {
      key: id,
      name: tc(t?.name ?? r.name ?? ''),
      city: t?.city ?? r.city ?? '',
      state: t?.state ?? r.state ?? '',
      type: t?.type ?? r.type ?? '',
      size: sizeOf(null, r.residents, !!r.censusEstimated),
      beds: t?.beds ?? null,
      residents: r.residents,
      amount: money(r.opportunityAnnual),
      amountN: Math.round(r.opportunityAnnual / 1000),
      per: money(r.reimbursementAnnual) + ' Part B at the national rate',
      est: !!r.censusEstimated,
      list,
      listRank: rank,
      row: t,
      dims: null,
      warmth: 0,
      tier: null,
      score: null,
      geo: t?.geo ?? r.geo ?? null,
      ehr: null,
    };
    applyRow(p, t);
    byId.set(id, p);
    out.push(p);
  };
  topRows.forEach((r, i) => {
    const id = r.facilityId as string;
    if (byId.has(id)) return;
    fromOpp(r, i < LIST_N ? 'top' : skipIds.has(id) ? 'skip' : 'mid', i + 1);
  });
  skipRows.forEach((r, i) => {
    const id = r.facilityId as string;
    const have = byId.get(id);
    if (have) {
      have.list = 'skip';
      have.listRank = i + 1;
    } else fromOpp(r, 'skip', i + 1);
  });
  for (const t of board) {
    if (byId.has(t.id)) continue;
    const p: Prospect = {
      key: t.id,
      name: tc(t.name),
      city: t.city ?? '',
      state: t.state ?? '',
      type: t.type ?? '',
      size: sizeOf(t.beds, null),
      beds: t.beds,
      residents: null,
      amount: null,
      amountN: 0,
      per: '',
      est: false,
      list: 'board',
      listRank: 0,
      row: t,
      dims: null,
      warmth: 0,
      tier: null,
      score: null,
      geo: t.geo ?? null,
      ehr: null,
    };
    applyRow(p, t);
    byId.set(t.id, p);
    out.push(p);
  }
  return out;
}

function applyRow(p: Prospect, t: TgTargetingRow | null) {
  if (!t) return;
  const d = t.dimensions;
  p.dims = [dim100(d.icp), dim100(d.urgency), dim100(d.ease), dim100(d.strategic)];
  p.warmth = t.warmth?.value ?? d.warmth ?? 0;
  p.tier = t.tier;
  p.score = t.priority;
  p.ehr = t.ehr?.current ?? null;
}

/** The board's meta line: "Austin, TX · SNF · 200 beds". */
export const boardMeta = (p: Prospect) =>
  [p.city && p.state ? p.city + ', ' + p.state : p.city || p.state, p.type, p.beds != null ? p.beds + ' beds' : p.residents ? residentsWord(p.residents, p.est) : '']
    .filter(Boolean)
    .join(' · ');

export interface Strip {
  facilities: number | null;
  total: number | null;
  avg: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  hist: number[];
  estimated: number | null;
}

/**
 * The reimbursement strip. The server's rollup (every matching facility, senior care only by default);
 * otherwise computed from the rows when every sized facility of the metro came back; and
 * nothing (en dashes) when only a page of the metro did.
 */
export function stripOf(top: TgOpportunityResponse | null, bins = 10): Strip {
  const empty: Strip = { facilities: null, total: null, avg: null, median: null, min: null, max: null, hist: [], estimated: null };
  if (!top) return empty;
  const r = top.rollup;
  if (r.totalOpportunityAnnual != null) {
    return {
      facilities: r.facilities,
      total: r.totalOpportunityAnnual,
      avg: r.avgOpportunityAnnual ?? null,
      median: r.medianOpportunityAnnual ?? null,
      min: r.minOpportunityAnnual ?? null,
      max: r.maxOpportunityAnnual ?? null,
      hist: r.histogram ?? [],
      estimated: r.censusEstimated ?? null,
    };
  }
  if (top.facilities.length < top.total) return { ...empty, facilities: top.total };
  const v = top.facilities.filter((x) => x.residents > 0).map((x) => x.opportunityAnnual).sort((a, b) => a - b);
  if (!v.length) return { ...empty, facilities: 0 };
  const sum = v.reduce((a, b) => a + b, 0);
  const mid = Math.floor(v.length / 2);
  const median = v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
  const min = v[0];
  const max = v[v.length - 1];
  const hist = new Array(bins).fill(0);
  for (const x of v) hist[max === min ? 0 : Math.min(bins - 1, Math.floor(((x - min) / (max - min)) * bins))]++;
  return {
    facilities: v.length,
    total: sum,
    avg: sum / v.length,
    median,
    min,
    max,
    hist,
    estimated: top.facilities.filter((x) => x.residents > 0 && x.censusEstimated).length,
  };
}

/** The design's quadrant rule and dot placement on the Ease x Strategic matrix (L20809, L20877). */
export const QUAD_SPLIT = 65;
export const axis = (v: number) => (v < QUAD_SPLIT ? 6 + (44 * Math.max(0, v - 25)) / (QUAD_SPLIT - 25) : 50 + (44 * Math.min(100 - QUAD_SPLIT, v - QUAD_SPLIT)) / (100 - QUAD_SPLIT));

/** "Why now" lines for the selected facility (design L20721-20727). */
export function whyNow(p: Prospect, metro: string, warmth: number, ehr: string | null): Array<[string, string]> {
  const why: Array<[string, string]> = [];
  if (p.list === 'top') why.push(['Top ' + p.listRank + ' in ' + metro + ' for uncaptured care-management dollars', 'MODELED']);
  if (p.dims && p.dims[1] >= 65) why.push(['Urgency ' + p.dims[1] + '/100, above the 65 bar', 'MODELED']);
  if (p.dims && p.dims[2] >= 70) why.push(['Easy to land: Ease ' + p.dims[2] + '/100', 'MODELED']);
  if (!warmth) why.push(['Never rated after a call: first conversation still open', '']);
  if (!ehr) why.push(['EHR not recorded: ask on the first call', '']);
  if (p.est) why.push(['Census is estimated, so the dollar figure is a range', 'EST']);
  return why;
}

/** US state abbreviation to its FIPS prefix, for the county map when the server names no counties. */
export const STATE_FIPS: Record<string, string> = {
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09', DE: '10', DC: '11', FL: '12', GA: '13', HI: '15', ID: '16', IL: '17',
  IN: '18', IA: '19', KS: '20', KY: '21', LA: '22', ME: '23', MD: '24', MA: '25', MI: '26', MN: '27', MS: '28', MO: '29', MT: '30', NE: '31',
  NV: '32', NH: '33', NJ: '34', NM: '35', NY: '36', NC: '37', ND: '38', OH: '39', OK: '40', OR: '41', PA: '42', RI: '44', SC: '45', SD: '46',
  TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53', WV: '54', WI: '55', WY: '56', PR: '72',
};
